import React, { useState, useEffect } from 'react';
import { Card, Button, Select, message, Typography, Checkbox, List, Slider, Input, Spin, Progress, Modal, Tag, Pagination } from 'antd';
import { ArrowLeftOutlined, ThunderboltOutlined, CheckCircleOutlined, LoadingOutlined, CopyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import { formatFileSize } from '../../helpers/file';
import { copyToClipboard } from '../../helpers/clipboard';
import './index.css';

const { Option } = Select;
const { Title, Paragraph } = Typography;
const { Search } = Input;

interface UploadRecord {
  id: number;
  fileName: string;
  url: string;
  rawUrl: string;
  contentType: string;
  time: string;
  size: string;
  uid: string;
}

const ImageCompress: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [quality, setQuality] = useState<number>(80);
  const [outputFormat, setOutputFormat] = useState<string>('original');
  const [compressing, setCompressing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [records, setRecords] = useState<UploadRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');

  const [progressModalVisible, setProgressModalVisible] = useState(false);
  const [progressInfo, setProgressInfo] = useState<{
    current: number;
    total: number;
    successCount: number;
    failCount: number;
    message: string;
    currentUrl: string;
    totalOriginalBytes: number;
    totalCompressedBytes: number;
    compressionRate: number | null;
    successItems: Array<{ fileName: string; recordUrl: string; originalSize?: number; compressedSize?: number; compressionRate?: number }>;
    failedItems: Array<{ url: string; message?: string; fileName?: string }>;
  }>({
    current: 0,
    total: 0,
    successCount: 0,
    failCount: 0,
    message: '',
    currentUrl: '',
    totalOriginalBytes: 0,
    totalCompressedBytes: 0,
    compressionRate: null,
    successItems: [],
    failedItems: [],
  });

  const outputFormatOptions = [
    { value: 'original', label: t('imageProcessing.tools.compress.outputFormatOriginal') },
    { value: 'webp', label: 'WebP' },
  ];

  const getUid = () => {
    return localStorage.getItem('fdd_uid') || '';
  };

  const getFileFormat = (fileName: string): string => {
    if (!fileName) return 'unknown';
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot === -1) return 'unknown';
    const ext = fileName.substring(lastDot + 1).toLowerCase();
    if (ext === 'jpeg') return 'jpg';
    return ext;
  };

  const getFileNameFromUrl = (url: string): string => {
    if (!url) return 'image';
    try {
      const path = url.split('?')[0];
      const segments = path.split('/');
      const last = segments[segments.length - 1];
      return last || 'image';
    } catch {
      return 'image';
    }
  };

  const loadHistory = async (currentPage: number = page, searchKeyword: string = keyword, currentPageSize: number = pageSize) => {
    setLoading(true);
    try {
      const uid = getUid();
      const response = await fetch(
        `/img/history?page=${currentPage}&pageSize=${currentPageSize}&keyword=${encodeURIComponent(searchKeyword)}&uid=${uid}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      const result = await response.json();
      if (result.data) {
        const imageRecords = result.data.filter((record: UploadRecord) =>
          record.contentType && record.contentType.startsWith('image/')
        );
        setRecords(imageRecords);
        setTotal(result.total);
      } else {
        message.error(t('imageProcessing.tools.compress.loadHistoryFail', { message: result.message || t('imageProcessing.tools.compress.loadHistoryFailUnknown') }));
      }
    } catch (error) {
      message.error(t('imageProcessing.tools.compress.networkError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory(1, '');
  }, []);

  const toggleSelect = (id: number) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === records.length &&
      records.every(record => selectedIds.includes(record.id))) {
      setSelectedIds(selectedIds.filter(id => !records.some(r => r.id === id)));
    } else {
      const currentPageIds = records.map(record => record.id);
      const combinedIds = [...selectedIds, ...currentPageIds];
      setSelectedIds(Array.from(new Set(combinedIds)));
    }
  };

  const handleBatchCompress = async () => {
    if (selectedIds.length === 0) {
      message.warning(t('imageProcessing.tools.compress.selectImagesFirst'));
      return;
    }

    const selectedRecords = records.filter(record => selectedIds.includes(record.id));
    const imageUrls = selectedRecords.map(record => record.url);
    const uid = getUid();

    setProgressModalVisible(true);
    setProgressInfo({
      current: 0,
      total: imageUrls.length,
      successCount: 0,
      failCount: 0,
      message: t('imageProcessing.tools.compress.preparingCompress'),
      currentUrl: '',
      totalOriginalBytes: 0,
      totalCompressedBytes: 0,
      compressionRate: null,
      successItems: [],
      failedItems: [],
    });
    setCompressing(true);

    try {
      const params = new URLSearchParams({
        imageUrls: JSON.stringify(imageUrls),
        quality: String(quality),
        uid: uid,
      });
      if (outputFormat === 'webp') {
        params.append('outputFormat', 'webp');
      }

      const eventSource = new EventSource(`/img/compress/batch?${params.toString()}`);

      eventSource.addEventListener('start', (event: MessageEvent) => {
        try {
          if (!event.data) return;
          const data = JSON.parse(event.data);
          setProgressInfo(prev => ({
            ...prev,
            total: data.total,
            message: data.message,
          }));
        } catch (e) {
          console.error('解析start事件失败:', e);
        }
      });

      eventSource.addEventListener('progress', (event: MessageEvent) => {
        try {
          if (!event.data) return;
          const data = JSON.parse(event.data);
          setProgressInfo(prev => ({
            ...prev,
            current: data.current,
            total: data.total,
            message: data.message,
            currentUrl: data.url,
          }));
        } catch (e) {
          console.error('解析progress事件失败:', e);
        }
      });

      eventSource.addEventListener('success', (event: MessageEvent) => {
        try {
          if (!event.data) return;
          const data = JSON.parse(event.data);
          const fileName = (data.url || '').split('/').pop()?.split('?')[0] || data.url || 'image';
          setProgressInfo(prev => ({
            ...prev,
            current: data.current,
            successCount: prev.successCount + 1,
            message: data.message,
            totalOriginalBytes: prev.totalOriginalBytes + (data.originalSize > 0 ? data.originalSize : 0),
            totalCompressedBytes: prev.totalCompressedBytes + (data.compressedSize || 0),
            compressionRate: data.compressionRate ?? prev.compressionRate,
            successItems: [...prev.successItems, {
              fileName,
              recordUrl: data.recordUrl || '',
              originalSize: data.originalSize > 0 ? data.originalSize : undefined,
              compressedSize: data.compressedSize,
              compressionRate: data.compressionRate,
            }],
          }));
        } catch (e) {
          console.error('解析success事件失败:', e);
        }
      });

      eventSource.addEventListener('compress-error', (event: MessageEvent) => {
        try {
          if (!event.data) return;
          const data = JSON.parse(event.data);
          const fileName = (data.url || '').split('/').pop()?.split('?')[0] || data.url || 'image';
          setProgressInfo(prev => ({
            ...prev,
            failCount: prev.failCount + 1,
            message: data.message,
            failedItems: [...prev.failedItems, { url: data.url || '', message: data.message, fileName }],
          }));
        } catch (e) {
          console.error('解析compress-error事件失败:', e);
        }
      });

      eventSource.addEventListener('error', (event: MessageEvent) => {
        try {
          if (!event.data) return;
          const data = JSON.parse(event.data);
          setProgressInfo(prev => ({
            ...prev,
            message: data.message || t('imageProcessing.tools.compress.compressFailed'),
          }));
          setTimeout(() => {
            eventSource.close();
            setProgressModalVisible(false);
            setCompressing(false);
            message.error(data.message || t('imageProcessing.tools.compress.compressFailed'));
          }, 2000);
        } catch (e) {
          console.error('解析error事件失败:', e);
        }
      });

      eventSource.addEventListener('complete', (event: MessageEvent) => {
        try {
          if (!event.data) return;
          const data = JSON.parse(event.data);
          eventSource.close();
          setProgressInfo(prev => ({
            ...prev,
            successCount: data.successCount,
            failCount: data.failCount,
            message: data.message,
            totalOriginalBytes: data.totalOriginalBytes ?? prev.totalOriginalBytes,
            totalCompressedBytes: data.totalCompressedBytes ?? prev.totalCompressedBytes,
            compressionRate: data.compressionRate ?? prev.compressionRate,
          }));
          setCompressing(false);
          setSelectedIds([]);
          loadHistory(page, keyword);
        } catch (e) {
          console.error('解析complete事件失败:', e);
        }
      });

      eventSource.onmessage = (event) => {
        try {
          if (!event.data) return;
          const data = JSON.parse(event.data);
          if (data.message) {
            setProgressInfo(prev => ({
              ...prev,
              message: data.message,
              currentUrl: data.url || prev.currentUrl,
            }));
          }
        } catch (e) {
          console.error('解析默认消息失败:', e);
        }
      };

      eventSource.onerror = () => {
        if (eventSource.readyState === EventSource.CLOSED) {
          if (compressing) {
            eventSource.close();
            setProgressModalVisible(false);
            setCompressing(false);
            message.error(t('imageProcessing.tools.compress.connectionError'));
          }
        }
      };
    } catch (error: unknown) {
      setProgressModalVisible(false);
      setCompressing(false);
      message.error((error as Error)?.message || t('imageProcessing.tools.compress.batchCompressError'));
    }
  };

  const handleSearch = (value: string) => {
    setKeyword(value);
    setPage(1);
    loadHistory(1, value);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    loadHistory(newPage, keyword);
  };

  const handlePageSizeChange = (current: number, newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
    loadHistory(1, keyword, newPageSize);
  };

  const isAllSelected = records.length > 0 &&
    records.every(record => selectedIds.includes(record.id));

  return (
    <div className="image-compress-page">
      <Card className="compress-card">
        <div className="compress-header">
          <Button
            type="text"
            size="small"
            icon={<ArrowLeftOutlined style={{ fontSize: 12 }} />}
            onClick={() => navigate('/image-processing')}
            className="back-button"
          >
            {t('imageProcessing.tools.compress.backToImageProcessing')}
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <Title level={5} style={{ margin: 0, fontSize: 16 }}>{t('imageProcessing.tools.compress.batchCompressTitle')}</Title>
            <Paragraph type="secondary" style={{ margin: 0, fontSize: 12 }}>
              {t('imageProcessing.tools.compress.batchCompressSubtitle')}
            </Paragraph>
          </div>
        </div>

        <div className="compress-content">
          <div className="compress-controls">
            <div className="control-item" style={{ minWidth: 180 }}>
              <div style={{ marginBottom: 2, fontWeight: 500, fontSize: 12 }}>{t('imageProcessing.tools.compress.selectQuality')}</div>
              <Slider
                min={60}
                max={95}
                value={quality}
                onChange={setQuality}
                marks={{ 60: '60', 75: '75', 80: '80', 90: '90', 95: '95' }}
              />
            </div>

            <div className="control-item">
              <div style={{ marginBottom: 2, fontWeight: 500, fontSize: 12 }}>{t('imageProcessing.tools.compress.outputFormat')}</div>
              <Select
                size="small"
                value={outputFormat}
                onChange={setOutputFormat}
                style={{ width: '100%', maxWidth: 140 }}
              >
                {outputFormatOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="control-item">
              <div style={{ marginBottom: 2, fontWeight: 500, fontSize: 12 }}>{t('imageProcessing.tools.compress.searchImages')}</div>
              <Search
                size="small"
                placeholder={t('imageProcessing.tools.compress.searchPlaceholder')}
                allowClear
                onSearch={handleSearch}
                style={{ width: '100%', maxWidth: 200 }}
              />
            </div>

            <div className="control-item" style={{ marginTop: 20 }}>
              <Button
                type="primary"
                size="small"
                icon={<ThunderboltOutlined style={{ fontSize: 12 }} />}
                onClick={handleBatchCompress}
                loading={compressing}
                disabled={selectedIds.length === 0 || compressing}
                className="compress-button"
              >
                {compressing ? t('imageProcessing.tools.compress.compressing') : t('imageProcessing.tools.compress.compressSelected', { count: selectedIds.length })}
              </Button>
            </div>
          </div>

          <div className="image-list-header">
            <Checkbox
              checked={isAllSelected}
              indeterminate={
                selectedIds.length > 0 &&
                selectedIds.length < records.filter(r => selectedIds.includes(r.id)).length
              }
              onChange={handleSelectAll}
            >
              <span style={{ fontSize: 12 }}>{t('imageProcessing.tools.compress.selectAllCurrentPage', { count: selectedIds.length })}</span>
            </Checkbox>
          </div>

          <Spin spinning={loading}>
            <List
              grid={{ gutter: 12, xs: 2, sm: 3, md: 4, lg: 5, xl: 6 }}
              dataSource={records}
              locale={{ emptyText: t('imageProcessing.tools.compress.noImages') }}
              renderItem={(record) => {
                const isSelected = selectedIds.includes(record.id);
                const fileFormat = getFileFormat(record.fileName);
                const formatColors: Record<string, string> = {
                  'jpg': '#ff4d4f',
                  'jpeg': '#ff4d4f',
                  'png': '#1890ff',
                  'gif': '#52c41a',
                  'webp': '#722ed1',
                  'bmp': '#fa8c16',
                };
                const formatColor = formatColors[fileFormat] || '#8c8c8c';
                return (
                  <List.Item>
                    <div
                      className={`image-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleSelect(record.id)}
                    >
                      <div className="image-checkbox">
                        <CheckCircleOutlined
                          className={isSelected ? 'checked' : 'unchecked'}
                        />
                      </div>
                      <div className="image-format-tag">
                        <Tag
                          color={formatColor}
                          style={{
                            margin: 0,
                            fontWeight: 'bold',
                            fontSize: '11px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: `1px solid ${formatColor}`,
                            textTransform: 'uppercase'
                          }}
                        >
                          {fileFormat}
                        </Tag>
                      </div>
                      <div className="image-wrapper">
                        <img
                          src={record.url}
                          alt={record.fileName}
                          loading="lazy"
                        />
                      </div>
                      <div className="image-name" title={record.fileName}>
                        {record.fileName}
                      </div>
                      <div className="image-footer">
                        <span className="image-size">{record.size || '-'}</span>
                        <Button
                          type="text"
                          size="small"
                          icon={<CopyOutlined />}
                          className="image-copy-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(record.url, t('common.linkCopied'));
                          }}
                          title={t('imageProcessing.tools.compress.copyLink')}
                        />
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          </Spin>

          {total > 0 && (
            <div className="pagination-wrapper">
              <Pagination
                size="small"
                current={page}
                total={total}
                pageSize={pageSize}
                onChange={handlePageChange}
                onShowSizeChange={handlePageSizeChange}
                showSizeChanger
                showTotal={(totalCount, range) => t('imageProcessing.tools.compress.paginationTotal', { from: range[0], to: range[1], total: totalCount })}
                pageSizeOptions={['12', '20', '40', '60', '100']}
              />
            </div>
          )}
        </div>
      </Card>

      <Modal
        title={compressing ? t('imageProcessing.tools.compress.progressModalTitle') : t('imageProcessing.tools.compress.compressComplete')}
        open={progressModalVisible}
        closable={!compressing}
        maskClosable={!compressing}
        footer={!compressing ? (
          <Button type="primary" onClick={() => setProgressModalVisible(false)}>
            {t('common.confirm')}
          </Button>
        ) : null}
        onCancel={() => setProgressModalVisible(false)}
        width={450}
      >
        <div style={{ padding: '16px 0' }}>
          <div style={{ marginBottom: 12, textAlign: 'center' }}>
            <Progress
              type="circle"
              percent={progressInfo.total > 0 ? Math.round((progressInfo.current / progressInfo.total) * 100) : 0}
              status={compressing ? 'active' : 'success'}
              format={() => `${progressInfo.current}/${progressInfo.total}`}
              size={120}
            />
          </div>

          <div style={{ marginBottom: 6, textAlign: 'center', fontWeight: 500, fontSize: 13 }}>
            {progressInfo.message}
          </div>

          {progressInfo.currentUrl && (
            <div style={{ marginBottom: 6, fontSize: 11, color: '#8c8c8c', wordBreak: 'break-all' }}>
              {t('imageProcessing.tools.compress.currentProcessing', { url: progressInfo.currentUrl })}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                {progressInfo.successCount}
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{t('common.success')}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#ff4d4f' }}>
                {progressInfo.failCount}
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{t('common.failed')}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                {progressInfo.total}
              </div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{t('common.total')}</div>
            </div>
          </div>

          {(progressInfo.totalOriginalBytes > 0 || progressInfo.totalCompressedBytes > 0 || progressInfo.compressionRate != null) && (
            <div style={{ marginTop: 16, padding: '12px', background: '#fafafa', borderRadius: 6 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                {t('imageProcessing.tools.compress.originalTotal')}: {formatFileSize(progressInfo.totalOriginalBytes)}
              </div>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                {t('imageProcessing.tools.compress.compressedTotal')}: {formatFileSize(progressInfo.totalCompressedBytes)}
              </div>
              {progressInfo.compressionRate != null && (
                <div style={{ fontSize: 12, fontWeight: 600, color: '#52c41a' }}>
                  {t('imageProcessing.tools.compress.compressionRate')}: {progressInfo.compressionRate.toFixed(1)}%
                </div>
              )}
            </div>
          )}

          {!compressing && (progressInfo.successItems.length > 0 || progressInfo.failedItems.length > 0) && (
            <div style={{ marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {t('imageProcessing.tools.compress.resultDetail')}
                </div>
                {progressInfo.successItems.length > 0 && (
                  <Button
                    type="link"
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(progressInfo.successItems.map(i => i.recordUrl).join('\n'), t('imageProcessing.tools.compress.allLinksCopied', { count: progressInfo.successItems.length }))}
                  >
                    {t('imageProcessing.tools.compress.copyAllLinks')}
                  </Button>
                )}
              </div>
              <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
                {progressInfo.successItems.map((item, idx) => (
                  <div key={idx} style={{ fontSize: 11, padding: '4px 0', borderBottom: idx < progressInfo.successItems.length - 1 ? '1px solid #f0f0f0' : 'none', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#52c41a', marginBottom: 2 }}>
                        {item.fileName}
                      </div>
                      <div style={{ color: '#666' }}>
                        {item.originalSize != null && item.compressedSize != null && item.compressionRate != null
                          ? `${formatFileSize(item.originalSize)} → ${formatFileSize(item.compressedSize)} (${item.compressionRate.toFixed(1)}%)`
                          : `${t('imageProcessing.tools.compress.compressedTotal')}: ${formatFileSize(item.compressedSize || 0)}`}
                      </div>
                    </div>
                    <Button
                      type="text"
                      size="small"
                      icon={<CopyOutlined />}
                      style={{ flexShrink: 0, padding: '0 4px', height: 24 }}
                      onClick={() => copyToClipboard(item.recordUrl, t('common.linkCopied'))}
                      title={t('imageProcessing.tools.compress.copyLink')}
                    />
                  </div>
                ))}
                {progressInfo.failedItems.map((item, idx) => (
                  <div key={`fail-${idx}`} style={{ fontSize: 11, padding: '4px 0', color: '#ff4d4f', borderBottom: idx < progressInfo.failedItems.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    {item.fileName || getFileNameFromUrl(item.url) || item.url}
                    {item.message && <span style={{ fontSize: 10, color: '#8c8c8c', marginLeft: 4 }}>({item.message})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default ImageCompress;
